import { useEffect, useState } from 'react';
import { useUserStore } from '@Auth/store';
import { Button } from '@components/ui/button';
import { Item, ItemActions, ItemContent, ItemDescription, ItemGroup, ItemMedia, ItemTitle } from '@components/ui/item';
import { cn } from 'cn';
import { Clock, ReceiptText, XIcon } from 'lucide-react';

import { useContractNavigation } from '../hooks/useContractNavigation';
import { useRecentStorage } from '../hooks/useRecentStorage';
import type { RecentContract, RecentSearch } from '../types';
import { getResultLink } from '../utils/get-result-link';

import { StatusDot } from './StatusDot';

const RecentSearchRow = ({
  id,
  recentSearch,
  removeRecentSearch,
  isActive,
  onHover,
  setQuery,
}: {
  id: string;
  recentSearch: RecentSearch;
  removeRecentSearch: (query: string) => void;
  isActive: boolean;
  onHover: () => void;
  setQuery: (query: string) => void;
}) => {
  return (
    <Item
      id={id}
      role="option"
      aria-selected={isActive}
      onMouseMove={onHover}
      size="xs"
      onMouseDown={(event) => {
        // mousedown (not click) so the input never loses focus
        event.preventDefault();
        setQuery(recentSearch.query);
      }}
      className={cn('hover:bg-muted cursor-pointer py-0 transition-colors', isActive && 'bg-accent')}
    >
      <ItemMedia variant="icon">
        <Clock />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>{recentSearch.query}</ItemTitle>
      </ItemContent>
      <ItemActions>
        <Button
          variant="ghost"
          size="icon"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            removeRecentSearch(recentSearch.query);
          }}
        >
          <XIcon className="shrink-0" />
        </Button>
      </ItemActions>
    </Item>
  );
};

const RecentContractRow = ({
  id,
  recentContract,
  removeRecentContract,
  isActive,
  onHover,
  goTo,
}: {
  id: string;
  recentContract: RecentContract;
  removeRecentContract: (id: string) => void;
  isActive: boolean;
  onHover: () => void;
  goTo: (link: string, openInNewTab: boolean) => void;
}) => {
  return (
    <Item
      id={id}
      role="option"
      aria-selected={isActive}
      onMouseMove={onHover}
      size="xs"
      className={cn('hover:bg-muted cursor-pointer transition-colors', isActive && 'bg-accent')}
      onMouseDown={(event) => {
        // mousedown (not click) so the input never loses focus
        event.preventDefault();
        goTo(getResultLink('submissions', recentContract.id), event.metaKey || event.ctrlKey);
      }}
    >
      <ItemMedia variant="icon">
        <ReceiptText />
      </ItemMedia>
      <ItemContent>
        <ItemTitle>{recentContract.name}</ItemTitle>
        {recentContract.ref && <ItemDescription>{recentContract.ref}</ItemDescription>}
      </ItemContent>
      <ItemContent>{recentContract.status && <StatusDot status={recentContract.status} />}</ItemContent>
      <ItemActions>
        <Button
          variant="ghost"
          size="icon"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            removeRecentContract(recentContract.id);
          }}
        >
          <XIcon className="size-4 shrink-0" />
        </Button>
      </ItemActions>
    </Item>
  );
};

export const RecentItemsList = ({ setQuery }: { setQuery: (query: string) => void }) => {
  const user = useUserStore(({ user }) => user);
  const { recentSearches, recentContracts, removeRecentSearch, removeRecentContract } = useRecentStorage(
    user?.username ?? '',
  );
  const recentSearchLength = recentSearches?.length ?? 0;
  const recentContractLength = recentContracts?.length ?? 0;
  const totalLength = recentSearchLength + recentContractLength;

  const goTo = useContractNavigation();

  const [activeIndex, setActiveIndex] = useState(0);
  useEffect(() => {
    setActiveIndex(0);
  }, [recentSearchLength, recentContractLength]);

  useEffect(() => {
    document.getElementById(`command-palette-item-${activeIndex}`)?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  useEffect(() => {
    if (totalLength === 0) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const delta = event.key === 'ArrowDown' ? 1 : -1;
        setActiveIndex((current) => (current + delta + totalLength) % totalLength);
        return;
      }

      if (event.key === 'Enter') {
        if (activeIndex < recentSearchLength) {
          const recentSearch = recentSearches[activeIndex];
          if (recentSearch) {
            event.preventDefault();
            setQuery(recentSearch.query);
          }
        } else {
          const recentContract = recentContracts[activeIndex - recentSearchLength];
          if (recentContract) {
            event.preventDefault();
            goTo(getResultLink('submissions', recentContract.id), event.metaKey || event.ctrlKey);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, totalLength, recentSearchLength, recentSearches, recentContracts, setQuery, goTo]);

  return (
    <div id="command-palette-list" role="listbox" className="min-h-24 flex-1 overflow-y-auto px-2 pb-2">
      {!!recentSearches.length && (
        <>
          <h3 className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-semibold tracking-wide uppercase">
            Recent searches
          </h3>

          <ItemGroup>
            {recentSearches.map((recentSearch, index) => (
              <RecentSearchRow
                id={`command-palette-item-${index}`}
                key={recentSearch.query}
                recentSearch={recentSearch}
                removeRecentSearch={removeRecentSearch}
                isActive={index === activeIndex}
                onHover={() => setActiveIndex(index)}
                setQuery={setQuery}
              />
            ))}
          </ItemGroup>
        </>
      )}

      {!!recentContracts.length && (
        <>
          <h3 className="text-muted-foreground px-3 pt-3 pb-1 text-xs font-semibold tracking-wide uppercase">
            Recent contracts
          </h3>
          <ItemGroup>
            {recentContracts.map((recentContract, index) => (
              <RecentContractRow
                id={`command-palette-item-${recentSearchLength + index}`}
                key={recentContract.id}
                recentContract={recentContract}
                removeRecentContract={removeRecentContract}
                isActive={recentSearchLength + index === activeIndex}
                onHover={() => setActiveIndex(recentSearchLength + index)}
                goTo={goTo}
              />
            ))}
          </ItemGroup>
        </>
      )}
    </div>
  );
};
